import { observer } from 'mobx-react-lite'
import React, { FocusEventHandler, useEffect, useRef } from 'react'
import { useTreeState } from '../../../stores/Root'
import { ISongStore } from '../../../stores/song/SongStore'
import { CaretHorizontal } from '../../core/icons'
import { DragWrapper, Input, InputContainer, Label, TempoContainer } from './common'
import TapToTempo from './TapToTempo'

export const TEMPO_RANGE = [64, 320]
const DRAG_MULTIPLIER = 0.8

interface Props {
  song: ISongStore
}

const useDragInput = (song: ISongStore, updateTempo: (value: number) => void) => {
  const { studio } = useTreeState()
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const element = ref.current
    let initialX = 0,
      initialValue = song.tempo

    const handleDragStart = (evt: MouseEvent) => {
      initialX = evt.clientX
      initialValue = song.tempo
      studio.disableAudioUpdates()
      document.addEventListener('mousemove', handleDragMove)
      document.addEventListener('mouseup', handleDragEnd)
      document.body.classList.add('resize')
    }

    const handleDragEnd = () => {
      initialX = 0
      studio.enableAudioUpdates()
      document.removeEventListener('mousemove', handleDragMove)
      document.removeEventListener('mouseup', handleDragEnd)
      document.body.classList.remove('resize')
    }

    const handleDragMove = (evt: MouseEvent) => {
      const deltaX = evt.clientX - initialX
      updateTempo(deltaX * DRAG_MULTIPLIER + initialValue)
    }

    element.addEventListener('mousedown', handleDragStart)

    return () => element.removeEventListener('mousedown', handleDragStart)
  }, [ref.current, updateTempo])
  return ref
}

const Tempo: React.FC<Props> = ({ song }) => {
  const tempoRef = React.createRef<HTMLInputElement>()

  const updateTempo = (tempo) => {
    let newTempo
    try {
      newTempo = parseInt(tempo)
    } catch {
      tempoRef.current.value = song.tempo.toString()
      return
    }
    newTempo = Math.max(Math.min(newTempo, TEMPO_RANGE[1]), TEMPO_RANGE[0])
    song.updateTempo(newTempo)
    if (tempoRef.current) tempoRef.current.value = newTempo
  }

  const dragRef = useDragInput(song, updateTempo)

  React.useEffect(() => {
    if (tempoRef.current) tempoRef.current.value = song.tempo.toString()
  }, [tempoRef.current, song.tempo])

  const handleBlur: FocusEventHandler<HTMLInputElement> = (e) => updateTempo(e.target.value)

  return (
    <TempoContainer>
      <Label>Tempo</Label>
      <InputContainer>
        <TapToTempo />
        <Input ref={tempoRef} onBlur={handleBlur} />
        <DragWrapper ref={dragRef}>{<CaretHorizontal width={'18'} height={'18'} />}</DragWrapper>
      </InputContainer>
    </TempoContainer>
  )
}

export default observer(Tempo)
